import { useEffect, useMemo, useState } from 'react';

import { Box, Link } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { NavLink as RouterLink } from 'react-router-dom';
import useSWR from 'swr';

import { financeConfigurationAtom } from '~atoms';
import { BillingsService } from '~features/finances';
import { useDebounce } from '~hooks';

import { useStyles } from './style';
import { SearchBar, Table } from '../../../../../components';
import { commaTimeStrings } from '../../../../../shared/datetime';
import {
	isEmptyString,
	isObject,
	isEmptyArray,
	instanceName,
	getFinanceTableHeaders,
} from '../../../../../shared/utility';
import * as actions from '../../../../../store/actions';

interface UserBookingsProps {
	financeType?: string;
	onFetchUserBillingBookings?(...args: unknown[]): unknown;
	date?: string;
	id?: string;
	userBillingBookings?: {
		data?: object;
		loading?: boolean;
		error?: object | string;
	};
	onFetchExpenseBookings?(...args: unknown[]): unknown;
}

const service = new BillingsService();

const UserBookings = (props: UserBookingsProps) => {
	const { date, id, onFetchUserBillingBookings, userBillingBookings } = props;
	const { t } = useTranslation();
	const financeConfiguration = useAtomValue(financeConfigurationAtom);

	const classes = useStyles();
	const [sortingCategory, setSortingCategory] = useState('id');
	const [order, setOrder] = useState('asc');
	const [orderDescending, setOrderDescending] = useState(true);

	const [pageNumber, setPageNumber] = useState(1);
	const [pageSize, setPageSize] = useState(20);

	const [searchValue, setSearchValue] = useState('');
	const [shouldSearch, setShouldSearch] = useState(false);

	const [isShowingSearchResults, setIsShowingSearchResults] = useState(false);

	const shouldSearchOnDebounce = false;

	const debouncedSearchValue = useDebounce(searchValue, 500);

	const fetchParameters = useMemo(() => {
		if (shouldSearch) {
			setShouldSearch(false);
		}

		return {
			searchQuery: searchValue,
			sortBy: sortingCategory,
			descending: orderDescending,
			page: pageNumber,
			pageSize: pageSize,
			excludeOneOffPayments: financeConfiguration.excludeOneOffPayments,
		};
	}, [
		pageNumber,
		pageSize,
		financeConfiguration.excludeOneOffPayments,
		sortingCategory,
		orderDescending,
		shouldSearch,
	]);

	const { data: userBillingBookingsData, isLoading: userBillingBookingsLoading } = useSWR(
		[
			service.basePath,
			date,
			service.usersSubPath,
			id?.toString(),
			service.bookingsSubPath,
			fetchParameters,
		],
		([_1, dateArg, _2, userIdArg, _3, args]) =>
			service.getBillingBookingsUnmapped(dateArg, userIdArg, args)
	);

	const handleSearchClick = () => {
		if (!isEmptyString(searchValue)) {
			setShouldSearch(true);
			setIsShowingSearchResults(true);
		}
		if (isEmptyString(searchValue) && isShowingSearchResults) {
			setShouldSearch(true);
			setIsShowingSearchResults(false);
		}
	};

	useEffect(() => {
		if (shouldSearchOnDebounce) {
			handleSearchClick();
		}
	}, [shouldSearchOnDebounce, debouncedSearchValue]);

	const handleResetSearch = () => {
		setSearchValue('');
		if (isShowingSearchResults === true) {
			setShouldSearch(true);
		}
		setIsShowingSearchResults(false);
	};

	const handleKeyUp = (e) => {
		switch (e.key) {
			case 'Enter':
				handleSearchClick();
				break;
			default:
				return;
		}
	};

	const events = {
		onClick: handleSearchClick,
		onChange: (e) => setSearchValue(e.target.value),
		onClear: handleResetSearch,
		onKeyUp: (e) => handleKeyUp(e),
	};

	const handleRequestSort = (property) => {
		const isDesc = sortingCategory === property && order === 'desc';
		setOrder(isDesc ? 'asc' : 'desc');
		setOrderDescending(isDesc);
		setSortingCategory(property);
	};

	const handlePageSizeChange = (newSize) => {
		setPageSize(newSize);
		setPageNumber(1);
	};

	const handlePageChange = (newPage) => {
		setPageNumber(newPage);
	};

	const headers = getFinanceTableHeaders(true, true, true);

	const tableHeader = [
		{ name: 'id', content: 'ID', hasSorting: true },
		{ name: 'start', content: t('views.ItemDetail.activities.start') },
		{ name: 'end', content: t('views.ItemDetail.activities.end') },
		{ name: 'user', content: t('views.ItemDetail.activities.user') },
		{ name: 'itemName', content: t('ui.label.itemName') },
		...headers,
	];

	const emptyBody =
		(
			isObject(userBillingBookingsData) &&
			!userBillingBookingsLoading &&
			isEmptyArray(userBillingBookingsData.results)
		) ?
			Array(1)
				.fill(Array(tableHeader.length).fill())
				.map((arr) => arr.map(() => ({ content: '-' })))
		:	null;

	const loadingBody =
		!isObject(userBillingBookingsData) || userBillingBookingsLoading ?
			Array(4)
				.fill(Array(tableHeader.length).fill())
				.map((arr) => arr.map(() => ({ loading: true })))
		:	null;

	const dataBody =
		isObject(userBillingBookingsData) && !userBillingBookingsLoading ?
			userBillingBookingsData.results.map((item) => {
				const currency = item.price.currencySymbol;
				const price = item.price.total;

				return [
					{
						content: (
							<Link
								color='primary'
								component={RouterLink}
								to={`/booking/booking-detail/${item.id}/summary`}
							>
								{item.id}
							</Link>
						),
					},
					{ content: commaTimeStrings(item.periodStart) },
					{ content: commaTimeStrings(item.periodEnd) },
					{ content: item.userReference.name },
					{ content: `${item.itemReference.name} - ${instanceName(item.itemInstance)}` },
					{ content: `${currency}${price.subtotal.toFixed(2)}` },
					{ content: `${currency}${price.discount.toFixed(2)}` },
					{ content: `${currency}${(price.subtotal - price.discount).toFixed(2)}` },
					{ content: `${currency}${price.totalVat.toFixed(2)}` },
					{ content: `${currency}${price.refund.toFixed(2)}` },
					{ content: `${currency}${price.total.toFixed(2)}` },
				];
			})
		:	Array(4)
				.fill(Array(tableHeader.length).fill())
				.map((arr) => arr.map(() => ({ loading: true })));

	return (
		<div>
			<Box pt={3}>
				<SearchBar
					hasSearchButton
					placeholder={t('views.finance.bookingDetails.placeholder')}
					searchEvents={events}
					searchValue={searchValue}
				/>
			</Box>
			<Table
				body={loadingBody || emptyBody || dataBody}
				cellStyle={classes.cellStyle}
				handlePageChange={handlePageChange}
				handleSorting={handleRequestSort}
				header={tableHeader}
				loading={userBillingBookingsLoading}
				order={order}
				orderBy={sortingCategory}
				page={pageNumber}
				rowsPerPage={pageSize}
				setRowsPerPage={handlePageSizeChange}
				title={t('nav.category.bookings')}
				total={isObject(userBillingBookingsData) ? userBillingBookingsData.total : 0}
			/>
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		userBillingBookings: state.paged.userBillingBookings,
	};
};

const mapDispatchToProps = (dispatch) => {
	return {
		onFetchUserBillingBookings: (dateTime, userId, page, filters) =>
			dispatch(actions.fetchUserBillingBookings(dateTime, userId, page, filters)),
	};
};

export default connect(mapStateToProps, mapDispatchToProps)(UserBookings);
